import apiClient from './config';
import { Category } from '../types/category';
import { getCurrentLanguage } from '../config/languageConfig';
import { buildQueryString } from './utils';
import { createCacheKey, getCacheData, setCacheData } from './cacheUtils';

/**
 * 이미지 경로를 환경에 맞게 변환하는 함수
 * @param relativePath 상대 경로
 * @returns 완전한 이미지 URL
 */
const getCategoryImageUrl = (relativePath: string): string => {
  if (process.env.NODE_ENV === 'development') {
    return `${process.env.PUBLIC_URL}${relativePath}`;
  }
  // GitHub Pages 배포 경로
  return `https://delivereee.github.io/home${relativePath}`;
};

/**
 * 모든 카테고리 가져오기
 * @returns 카테고리 목록
 */
export const getCategories = async (): Promise<Category[]> => {
  try {
    // 현재 설정된 언어 가져오기
    const currentLang = getCurrentLanguage();
    
    // 쿼리 파라미터와 엔드포인트
    const endpoint = '/api/dee-categories';
    const params = { lang: currentLang };
    
    // 캐시 키 생성
    const cacheKey = createCacheKey(endpoint, params);
    
    // 캐시된 데이터 확인
    const cachedData = getCacheData<Category[]>(cacheKey, currentLang);
    if (cachedData) {
      return cachedData;
    }
    
    // 쿼리 파라미터 생성
    const queryString = buildQueryString(params);
    
    // API에서 카테고리 데이터 가져오기 (언어 설정 포함)
    const response = await apiClient.get(`${endpoint}${queryString}`);
    
    if (response.data && Array.isArray(response.data)) {
      // API 응답 데이터를 Category 인터페이스에 맞게 변환
      const categories = response.data.map((item: any) => {
        const category: Category = {
          id: item.id,
          name: item.name, // 기본 이름 사용
          nameKo: item.nameKo,
          nameEn: item.nameEn,
          nameJa: item.nameJa,
          nameZhCn: item.nameZhCn,
          nameZhTw: item.nameZhTw,
          imageUrl: getCategoryImageUrl(item.imageUrl) // 환경에 맞게 이미지 경로 변환
        };

        // giftshop 카테고리의 경우 직접 가게 상세 페이지로 이동하도록 설정
        if (item.id === 'giftshop') {
          category.directShopId = 'gift-shop-1';
          category.routeType = 'direct-shop';
        } else {
          category.routeType = 'list';
        }

        return category;
      });
      
      // 변환된 데이터 캐싱
      setCacheData(cacheKey, categories, currentLang);
      
      return categories;
    }
    
    // 응답이 배열이 아니거나 비어있으면 빈 배열 반환
    console.warn('API가 유효하지 않은 카테고리 데이터를 반환했습니다.');
    return [];
  } catch (error) {
    console.error('API에서 카테고리 데이터를 가져오는데 실패했습니다:', error);
    // API 호출 실패 시 빈 배열 반환
    return [];
  }
};